/**
 * In-game calendar helpers. The daily ritual is anchored to the REAL calendar
 * (CLAUDE.md: "one expedition per in-game day, anchored to the real calendar"), so
 * one in-game day advances per whole real-calendar day the player was away.
 *
 * `inGameDate` is a simple ISO-like `YYYY-MM-DD` string (see save.ts). These helpers
 * are pure + UTC-based so they never depend on the host timezone or Date.now().
 */

const MS_PER_DAY = 86_400_000;

/** UTC-midnight epoch ms for a year/month/day, correct for years < 100. */
function utcMidnight(year: number, monthIndex: number, day: number): number {
  // Date.UTC maps years 0–99 to 1900–1999; setUTCFullYear restores the real year.
  const d = new Date(0);
  d.setUTCFullYear(year, monthIndex, day);
  d.setUTCHours(0, 0, 0, 0);
  return d.getTime();
}

/** Parse `YYYY-MM-DD` into a UTC-midnight epoch ms. NaN-safe: bad input ⇒ day 1. */
export function parseInGameDate(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (!m) return utcMidnight(1, 0, 1);
  const [, y, mo, d] = m;
  const ms = utcMidnight(Number(y), Number(mo) - 1, Number(d));
  return Number.isFinite(ms) ? ms : utcMidnight(1, 0, 1);
}

/** Format a UTC-midnight epoch ms back to `YYYY-MM-DD`. */
export function formatInGameDate(ms: number): string {
  const d = new Date(ms);
  const y = String(d.getUTCFullYear()).padStart(4, '0');
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/** Advance an in-game date by `days` whole days (clamped to ≥ 0). */
export function addInGameDays(date: string, days: number): string {
  const d = Math.max(0, Math.trunc(days));
  return formatInGameDate(parseInGameDate(date) + d * MS_PER_DAY);
}

/**
 * Whole real-calendar days between two wall-clock ms values, floored at 0. Used to
 * decide how many in-game days the overnight tick should replay on return.
 */
export function wallClockDaysElapsed(fromMs: number, toMs: number): number {
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return 0;
  const diff = toMs - fromMs;
  if (diff <= 0) return 0;
  return Math.floor(diff / MS_PER_DAY);
}
